import { useState, useEffect, useMemo } from "react";
import { api } from "@/lib/rn3";
import { toast } from "sonner";
import { Download, Loader2, AlertTriangle, FileCheck, Send, X, Eye, Users, CheckCircle2 } from "lucide-react";

const fmt = (n) => `${n < 0 ? "-" : ""}$${Math.abs(Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const currentYear = () => new Date().getFullYear();

/** Modal: send the year-end 1099 summary email to one vendor. */
const Send1099Modal = ({ open, onClose, bookId, vendorRow, year, threshold, lang, onSent }) => {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open && vendorRow) { setTo(vendorRow.email || ""); setSubject(""); setMessage(""); } }, [open, vendorRow]);

  if (!open || !vendorRow) return null;

  const previewPdf = async () => {
    try {
      const { data } = await api.get(`/back-office/books/${bookId}/reports/1099-nec/${vendorRow.vendor_id}/preview.pdf`, { params: { year, threshold }, responseType: "blob" });
      const url = window.URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch { toast.error("Preview failed"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!to) { toast.error(lang === "es" ? "Email requerido" : "Recipient email required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/back-office/books/${bookId}/reports/1099-nec/${vendorRow.vendor_id}/send`, { to, subject: subject || undefined, message: message || undefined }, { params: { year, threshold } });
      if (data?.mocked) toast.success(lang === "es" ? "Resumen enviado (modo prueba — sin clave Resend)" : "Summary sent (MOCKED — no Resend key configured)");
      else toast.success(lang === "es" ? "Resumen enviado" : "Summary sent");
      onSent?.(data);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="send-1099-modal">
      <div className="bg-white max-w-lg w-full border border-[#E5E5E5] shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white">
          <div>
            <div className="rn3-eyebrow">{lang === "es" ? `Resumen 1099 · ${year}` : `1099 Summary · ${year}`}</div>
            <h3 className="font-serif-display text-2xl mt-1">{vendorRow.name}</h3>
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-[#8B1C22]"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="border border-[#E5E5E5] p-3"><div className="rn3-eyebrow">{lang === "es" ? "Total Pagado" : "Total Paid"}</div><div className="font-serif-display text-xl mt-1 tabular-nums">${Number(vendorRow.total_paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>
            <div className="border border-[#E5E5E5] p-3"><div className="rn3-eyebrow">{lang === "es" ? "Reportable" : "Reportable"}</div><div className={`font-serif-display text-xl mt-1 ${vendorRow.meets_threshold ? "text-[#15803D]" : "text-[#737373]"}`}>{vendorRow.meets_threshold ? (lang === "es" ? "Sí" : "Yes") : "No"}</div></div>
            <div className="border border-[#E5E5E5] p-3"><div className="rn3-eyebrow">Tax ID</div><div className={`font-mono text-sm mt-2 ${vendorRow.missing_tax_id && vendorRow.meets_threshold ? "text-[#8B1C22]" : ""}`}>{vendorRow.tax_id || (vendorRow.meets_threshold ? (lang === "es" ? "FALTA" : "MISSING") : "—")}</div></div>
          </div>

          {vendorRow.missing_tax_id && vendorRow.meets_threshold && (
            <div className="border border-[#8B1C22] bg-[#FDF5F5] px-3 py-2 text-xs text-[#8B1C22] flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{lang === "es" ? "Falta el Tax ID. El correo incluirá una solicitud automática del Formulario W-9." : "Tax ID is missing. The email will automatically include a W-9 request."}</span>
            </div>
          )}

          <div>
            <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Destinatario" : "To"}</label>
            <input required type="email" value={to} onChange={(e) => setTo(e.target.value)} className="rn3-input" data-testid="send-1099-to" />
          </div>
          <div>
            <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Asunto (opcional)" : "Subject (optional)"}</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="rn3-input" data-testid="send-1099-subject" placeholder={`1099-NEC Summary for Tax Year ${year} — Roman Numerals III`} />
          </div>
          <div>
            <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Mensaje (opcional)" : "Message (optional)"}</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} className="rn3-input" data-testid="send-1099-message" placeholder={lang === "es" ? "Mensaje personalizado…" : "Custom message…"} />
          </div>
          <div className="text-xs text-[#737373] border border-[#F3EFE9] bg-[#FDFBF7] px-3 py-2">
            {lang === "es" ? "Se adjuntará un PDF de resumen 1099 con su marca." : "A branded 1099 summary PDF will be attached."}
          </div>
          <div className="flex items-center justify-between gap-3 pt-2 border-t border-[#E5E5E5]">
            <button type="button" onClick={previewPdf} className="rn3-btn-gold" data-testid="send-1099-preview">
              <Eye className="w-4 h-4" /> {lang === "es" ? "Vista Previa PDF" : "Preview PDF"}
            </button>
            <div className="flex items-center gap-2">
              <button type="button" onClick={onClose} className="text-sm text-[#737373] hover:text-[#0A0A0A] uppercase tracking-widest">{lang === "es" ? "Cancelar" : "Cancel"}</button>
              <button disabled={busy} className="rn3-btn-primary" data-testid="send-1099-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {busy ? "…" : (lang === "es" ? "Enviar Resumen" : "Send Summary")}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

/**
 * 1099-NEC prep panel. Lives inside BillsTab.
 * Lists vendors flagged is_1099 with year-to-date paid totals, threshold check, and CSV export.
 */
/** Modal: bulk-send year-end 1099 summaries to many vendors at once. */
const BulkSend1099Modal = ({ open, onClose, bookId, year, threshold, vendors, lang, onSent }) => {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [exclude, setExclude] = useState(() => new Set()); // vendor_ids to skip
  const [result, setResult] = useState(null);

  useEffect(() => { if (open) { setMessage(""); setExclude(new Set()); setResult(null); } }, [open]);
  if (!open) return null;

  // Eligible = reportable + has email (we still show all in the list for transparency)
  const eligible = vendors.filter((v) => v.meets_threshold);
  const willSend = eligible.filter((v) => !exclude.has(v.vendor_id) && (v.email || "").trim());
  const noEmail = eligible.filter((v) => !(v.email || "").trim());

  const toggle = (id) => setExclude((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const submit = async (e) => {
    e.preventDefault();
    if (willSend.length === 0) { toast.error(lang === "es" ? "Ningún proveedor a quien enviar" : "No vendors to send to"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/back-office/books/${bookId}/reports/1099-nec/bulk-send`, { message: message || undefined, only_above: true, include_vendor_ids: willSend.map((v) => v.vendor_id) }, { params: { year, threshold } });
      setResult(data);
      const note = data.mocked ? (lang === "es" ? " (modo prueba — sin clave Resend)" : " (MOCKED — no Resend key configured)") : "";
      toast.success((lang === "es" ? `${data.sent_count} resúmenes enviados` : `${data.sent_count} summaries sent`) + note);
      onSent?.(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Bulk send failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="bulk-1099-modal">
      <div className="bg-white max-w-2xl w-full border border-[#E5E5E5] shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white">
          <div>
            <div className="rn3-eyebrow">{lang === "es" ? `Envío Masivo · Año ${year}` : `Bulk Send · Tax Year ${year}`}</div>
            <h3 className="font-serif-display text-2xl mt-1">{lang === "es" ? "Resúmenes 1099-NEC" : "1099-NEC Summaries"}</h3>
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-[#8B1C22]"><X className="w-5 h-5" /></button>
        </div>

        {result ? (
          <div className="p-6 space-y-4" data-testid="bulk-1099-result">
            <div className="border border-[#15803D] bg-[#F0FDF4] px-4 py-3 flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#15803D] mt-0.5" />
              <div>
                <div className="font-medium">{lang === "es" ? `${result.sent_count} resúmenes enviados` : `${result.sent_count} summaries sent`}</div>
                {result.mocked && <div className="text-xs text-[#737373] mt-1">{lang === "es" ? "Modo prueba — agrega RESEND_API_KEY para envíos reales." : "MOCKED — add RESEND_API_KEY to enable real sends."}</div>}
              </div>
            </div>
            {result.skipped_count > 0 && (
              <div className="border border-[#E5E5E5] bg-[#FDFBF7] px-4 py-3">
                <div className="rn3-eyebrow mb-2">{lang === "es" ? `${result.skipped_count} omitidos` : `${result.skipped_count} skipped`}</div>
                <ul className="space-y-1 text-sm">
                  {result.skipped.map((s) => (
                    <li key={s.vendor_id} className="flex justify-between"><span>{s.name}</span><span className="text-xs uppercase tracking-widest text-[#737373]">{s.reason.replace(/_/g, " ")}</span></li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex justify-end pt-2"><button onClick={onClose} className="rn3-btn-primary" data-testid="bulk-1099-close">{lang === "es" ? "Cerrar" : "Close"}</button></div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="border border-[#E5E5E5] p-3"><div className="rn3-eyebrow">{lang === "es" ? "Elegibles" : "Eligible"}</div><div className="font-serif-display text-2xl mt-1">{eligible.length}</div></div>
              <div className="border border-[#15803D] p-3 bg-[#F0FDF4]"><div className="rn3-eyebrow">{lang === "es" ? "Se enviarán" : "Will send"}</div><div className="font-serif-display text-2xl mt-1 text-[#15803D]">{willSend.length}</div></div>
              <div className={`border p-3 ${noEmail.length > 0 ? "border-[#8B1C22] bg-[#FDF5F5]" : "border-[#E5E5E5]"}`}><div className="rn3-eyebrow">{lang === "es" ? "Sin email" : "No email"}</div><div className={`font-serif-display text-2xl mt-1 ${noEmail.length > 0 ? "text-[#8B1C22]" : ""}`}>{noEmail.length}</div></div>
            </div>

            {eligible.length === 0 ? (
              <div className="border border-[#E5E5E5] bg-[#FDFBF7] px-4 py-6 text-center text-sm text-[#737373]">
                {lang === "es" ? `No hay proveedores reportables (≥ $${threshold}) en ${year}.` : `No reportable vendors (≥ $${threshold}) for ${year}.`}
              </div>
            ) : (
              <div className="border border-[#E5E5E5] max-h-64 overflow-y-auto">
                <table className="w-full text-sm" data-testid="bulk-1099-list">
                  <thead className="text-left text-xs uppercase tracking-widest text-[#737373] border-b border-[#E5E5E5] bg-[#FDFBF7] sticky top-0">
                    <tr>
                      <th className="py-2 px-3 w-8"></th>
                      <th className="py-2 px-3">{lang === "es" ? "Proveedor" : "Vendor"}</th>
                      <th className="py-2 px-3">Email</th>
                      <th className="py-2 px-3 text-right">{lang === "es" ? "Total" : "Total"}</th>
                      <th className="py-2 px-3">Tax ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eligible.map((v) => {
                      const noEm = !(v.email || "").trim();
                      const excluded = exclude.has(v.vendor_id);
                      return (
                        <tr key={v.vendor_id} className={`border-b border-[#F3EFE9] ${noEm ? "opacity-50" : ""}`} data-testid={`bulk-1099-row-${v.vendor_id}`}>
                          <td className="py-2 px-3">
                            <input type="checkbox" checked={!excluded && !noEm} disabled={noEm} onChange={() => toggle(v.vendor_id)} data-testid={`bulk-1099-toggle-${v.vendor_id}`} />
                          </td>
                          <td className="py-2 px-3 font-medium">{v.name}</td>
                          <td className="py-2 px-3 text-xs text-[#737373]">{v.email || <span className="text-[#8B1C22] uppercase tracking-widest">{lang === "es" ? "Falta" : "Missing"}</span>}</td>
                          <td className="py-2 px-3 text-right tabular-nums">${Number(v.total_paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="py-2 px-3 font-mono text-xs">{v.tax_id || (v.missing_tax_id ? <span className="inline-flex items-center gap-1 text-[#8B1C22]"><AlertTriangle className="w-3 h-3" />W-9</span> : "—")}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div>
              <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Mensaje opcional para todos" : "Optional message for all recipients"}</label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} className="rn3-input" data-testid="bulk-1099-message" placeholder={lang === "es" ? "Mensaje que se incluirá en cada email…" : "Will be included in every email…"} />
            </div>
            <div className="text-xs text-[#737373] border border-[#F3EFE9] bg-[#FDFBF7] px-3 py-2">
              {lang === "es" ? "Cada proveedor recibirá su propio PDF con la marca de tu firma. Las casillas desmarcadas no se enviarán." : "Each vendor receives their own branded PDF. Unchecked rows are skipped."}
            </div>
            <div className="flex items-center justify-between gap-3 pt-2 border-t border-[#E5E5E5]">
              <span className="text-xs text-[#737373]">{willSend.length} {lang === "es" ? "destinatarios" : "recipients"}</span>
              <div className="flex items-center gap-2">
                <button type="button" onClick={onClose} className="text-sm text-[#737373] hover:text-[#0A0A0A] uppercase tracking-widest">{lang === "es" ? "Cancelar" : "Cancel"}</button>
                <button disabled={busy || willSend.length === 0} className="rn3-btn-primary" data-testid="bulk-1099-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {busy ? "…" : (lang === "es" ? `Enviar ${willSend.length}` : `Send ${willSend.length}`)}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};


const Report1099Panel = ({ bookId, lang }) => {
  const [year, setYear] = useState(currentYear() - 1);
  const [threshold, setThreshold] = useState(600);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [onlyAbove, setOnlyAbove] = useState(true);
  const [sendRow, setSendRow] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/back-office/books/${bookId}/reports/1099-nec`, { params: { year, threshold } });
      setData(d);
    } catch (err) { toast.error("Could not load 1099 report"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bookId]);

  const downloadCsv = async () => {
    try {
      const { data: blob } = await api.get(`/back-office/books/${bookId}/reports/1099-nec.csv`, { params: { year, threshold, only_above: onlyAbove }, responseType: "blob" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `1099-NEC-${year}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 4000);
    } catch { toast.error("CSV download failed"); }
  };

  const rows = useMemo(() => {
    if (!data?.vendors) return [];
    return onlyAbove ? data.vendors.filter((r) => r.meets_threshold) : data.vendors;
  }, [data, onlyAbove]);

  return (
    <div className="space-y-5" data-testid="report-1099-panel">
      <div className="rn3-card">
        <div className="rn3-eyebrow">{lang === "es" ? "Preparación 1099-NEC" : "1099-NEC Prep"}</div>
        <p className="text-sm text-[#737373] mt-2">
          {lang === "es"
            ? "Totales pagados durante el año a proveedores marcados como 1099. La línea base IRS es $600."
            : "Year-to-date payments to vendors flagged as 1099. IRS reporting threshold is $600."}
        </p>
        <div className="flex flex-wrap items-end gap-3 mt-4">
          <div>
            <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Año" : "Tax Year"}</label>
            <input type="number" min="2020" max="2099" value={year} onChange={(e) => setYear(Number(e.target.value || currentYear()))} className="rn3-input w-28" data-testid="r1099-year" />
          </div>
          <div>
            <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Umbral $" : "Threshold $"}</label>
            <input type="number" min="0" step="50" value={threshold} onChange={(e) => setThreshold(Number(e.target.value || 0))} className="rn3-input w-28" data-testid="r1099-threshold" />
          </div>
          <button onClick={load} className="rn3-btn-gold" data-testid="r1099-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileCheck className="w-4 h-4" />}
            {lang === "es" ? "Generar" : "Generate"}
          </button>
          <label className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#737373] ml-2">
            <input type="checkbox" checked={onlyAbove} onChange={(e) => setOnlyAbove(e.target.checked)} data-testid="r1099-only-above" />
            {lang === "es" ? `Solo ≥ $${threshold}` : `Only ≥ $${threshold}`}
          </label>
          <div className="ml-auto">
            <button onClick={() => setBulkOpen(true)} className="rn3-btn-gold" data-testid="r1099-bulk-send" disabled={!data || !data.vendors || data.vendors.filter((v) => v.meets_threshold).length === 0}>
              <Users className="w-4 h-4" /> {lang === "es" ? "Envío Masivo" : "Bulk Send"}
            </button>
            <button onClick={downloadCsv} className="rn3-btn-primary ml-2" data-testid="r1099-download-csv">
              <Download className="w-4 h-4" /> {lang === "es" ? "Exportar CSV" : "Export CSV"}
            </button>
          </div>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="border border-[#E5E5E5] p-3"><div className="rn3-eyebrow">{lang === "es" ? "Proveedores 1099" : "1099 Vendors"}</div><div className="font-serif-display text-2xl mt-1">{data.summary.total_vendors}</div></div>
          <div className="border border-[#E5E5E5] p-3"><div className="rn3-eyebrow">{lang === "es" ? `≥ $${data.threshold}` : `Above $${data.threshold}`}</div><div className="font-serif-display text-2xl mt-1 text-[#15803D]">{data.summary.above_threshold}</div></div>
          <div className="border border-[#E5E5E5] p-3"><div className="rn3-eyebrow">{lang === "es" ? "Sin Tax ID" : "Missing Tax ID"}</div><div className={`font-serif-display text-2xl mt-1 ${data.summary.missing_tax_id > 0 ? "text-[#8B1C22]" : ""}`}>{data.summary.missing_tax_id}</div></div>
          <div className="border border-[#E5E5E5] p-3"><div className="rn3-eyebrow">{lang === "es" ? "Total Pagado" : "Total Paid"}</div><div className="font-serif-display text-2xl mt-1 tabular-nums">{fmt(data.summary.grand_total)}</div></div>
        </div>
      )}

      <div className="border border-[#E5E5E5] overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]" data-testid="r1099-table">
          <thead className="text-left text-xs uppercase tracking-widest text-[#737373] border-b border-[#E5E5E5] bg-[#FDFBF7]">
            <tr>
              <th className="py-3 px-3">{lang === "es" ? "Proveedor" : "Vendor"}</th>
              <th className="py-3 px-3">Tax ID</th>
              <th className="py-3 px-3">{lang === "es" ? "Dirección" : "Address"}</th>
              <th className="py-3 px-3 text-right">{lang === "es" ? "Pagado de Facturas" : "Bill Paid"}</th>
              <th className="py-3 px-3 text-right">{lang === "es" ? "Pagado Directo" : "Direct Paid"}</th>
              <th className="py-3 px-3 text-right">{lang === "es" ? "Total" : "Total"}</th>
              <th className="py-3 px-3">{lang === "es" ? "Estado" : "Status"}</th>
              <th className="py-3 px-3 text-right">{lang === "es" ? "Acciones" : "Actions"}</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="8" className="py-8 text-center text-[#737373]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan="8" className="py-12 text-center text-[#737373]">{lang === "es" ? "Ningún proveedor 1099 cumple los criterios para este año." : "No 1099 vendors meet the criteria for this year."}</td></tr>}
            {rows.map((r) => (
              <tr key={r.vendor_id} className="border-b border-[#F3EFE9] hover:bg-[#FDFBF7]" data-testid={`r1099-row-${r.vendor_id}`}>
                <td className="py-2 px-3">
                  <div className="font-medium">{r.name}</div>
                  {r.company && <div className="text-xs text-[#737373]">{r.company}</div>}
                </td>
                <td className={`py-2 px-3 font-mono text-xs ${r.missing_tax_id && r.meets_threshold ? "text-[#8B1C22]" : "text-[#404040]"}`}>
                  {r.tax_id || (r.meets_threshold ? <span className="inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {lang === "es" ? "FALTA" : "MISSING"}</span> : "—")}
                </td>
                <td className="py-2 px-3 text-xs text-[#737373] max-w-[260px] truncate">{r.address || "—"}</td>
                <td className="py-2 px-3 text-right tabular-nums">{fmt(r.bill_paid)}</td>
                <td className="py-2 px-3 text-right tabular-nums">{fmt(r.direct_paid)}</td>
                <td className="py-2 px-3 text-right tabular-nums font-medium">{fmt(r.total_paid)}</td>
                <td className="py-2 px-3">
                  {r.meets_threshold
                    ? <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-[#15803D] text-[#15803D]">{lang === "es" ? "Reportable" : "Reportable"}</span>
                    : <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-[#737373] text-[#737373]">{lang === "es" ? "Bajo umbral" : "Below"}</span>
                  }
                </td>
                <td className="py-2 px-3 text-right">
                  <button
                    type="button"
                    onClick={() => setSendRow(r)}
                    className="inline-flex items-center gap-1 text-xs uppercase tracking-widest px-2 py-1 border border-[#C4A252] text-[#8B1C22] hover:bg-[#FDFBF7]"
                    data-testid={`r1099-send-${r.vendor_id}`}
                    title={lang === "es" ? "Enviar resumen al proveedor" : "Email summary to vendor"}
                  >
                    <Send className="w-3 h-3" /> {lang === "es" ? "Enviar" : "Email"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Send1099Modal open={!!sendRow} onClose={() => setSendRow(null)} bookId={bookId} vendorRow={sendRow} year={year} threshold={threshold} lang={lang} onSent={() => load()} />
      <BulkSend1099Modal open={bulkOpen} onClose={() => setBulkOpen(false)} bookId={bookId} year={year} threshold={threshold} vendors={data?.vendors || []} lang={lang} onSent={() => load()} />
    </div>
  );
};

export default Report1099Panel;
